"use client";

import React, { useState } from "react";
import { Facebook, Linkedin, Twitter } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider } from "@/integrations/forms/FormProvider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="205553" className="flex flex-col space-y-4">
        {[1, 2, 3, 4, 5].map((_, i) => <div key={i} data-index={i} className="h-4 w-24 bg-muted animate-pulse rounded" />)}
      </div>;
  }
  return <div className="flex flex-col space-y-3">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="text-muted-foreground hover:text-primary transition-colors text-sm font-default font-medium">
          {item.label}
        </Link>)}
    </div>;
}
export default function AgencyFootnote() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  return <section id="footer-informatie" className="dark bg-background text-foreground pt-16 pb-8">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
          <div className="flex flex-col items-start space-y-6">
            <Link to="/">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1629897/img/image_dj1nbgq9.png" alt="EduMedius Logo" className="h-7" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-sm font-default">
              EduMedius is uw strategische partner in het onderwijs. Wij slaan de brug tussen onderwijsinstellingen en professionals, met een focus op kwaliteit, ontzorging en de Wet DBA. EduMedius – midden in het onderwijs.
            </p>
          </div>

          <div className="flex flex-col space-y-6">
            <h3 className="font-default font-bold text-lg tracking-wider uppercase text-foreground">
              Navigatie
            </h3>
            <MenuProvider menu_id="16">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          <div className="flex flex-col items-start space-y-6">
            <h3 className="font-default font-bold text-lg tracking-wider uppercase text-foreground">
              Samenwerken
            </h3>
            <p className="text-muted-foreground text-sm mb-2 font-default">
              Wilt u de kwaliteit van uw onderwijs versterken? EduMedius denkt graag mee in uw capaciteitsvraagstuk – plan een vrijblijvend gesprek.
            </p>
            <Button size="lg" onClick={() => setIsDialogOpen(true)} className="w-full sm:w-auto font-default font-bold uppercase tracking-widest bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg transition-all">
              Plan een Gesprek
            </Button>
          </div>
        </div>

        <Separator className="bg-border/20 mb-8" />

        <div className="flex flex-col lg:flex-row items-center justify-between gap-6 text-sm text-muted-foreground">
          <p className="text-center lg:text-left max-w-2xl font-default">
            EduMedius.nl is een initiatief voortgekomen uit Slim Academy en ScriptieMaster. Gevestigd in het hart van het onderwijs. © Alle rechten voorbehouden.
          </p>
          
          <div className="flex flex-wrap justify-center items-center gap-6">
            <div className="flex items-center space-x-6">
              <Link to="#" className="hover:text-primary transition-colors font-default">
                Privacyverklaring
              </Link>
              <Link to="#" className="hover:text-primary transition-colors font-default">
                Algemene Voorwaarden
              </Link>
            </div>
            
            <div className="flex items-center space-x-2">
              <Link to="#" className="p-2 rounded-full bg-muted/30 hover:bg-primary hover:text-primary-foreground transition-all duration-300" aria-label="Facebook">
                <Facebook className="size-4" />
              </Link>
              <Link to="https://www.linkedin.com/company/edumedius/" className="p-2 rounded-full bg-muted/30 hover:bg-primary hover:text-primary-foreground transition-all duration-300" aria-label="LinkedIn">
                <Linkedin className="size-4" />
              </Link>
              <Link to="#" className="p-2 rounded-full bg-muted/30 hover:bg-primary hover:text-primary-foreground transition-all duration-300" aria-label="Twitter">
                <Twitter className="size-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md bg-card border-border dark">
          <DialogHeader>
            <DialogTitle className="font-default text-xl font-bold text-foreground">Plan een Gesprek</DialogTitle>
            <DialogDescription className="font-default text-muted-foreground">
              Vul uw gegevens in en wij nemen zo spoedig mogelijk contact met u op.
            </DialogDescription>
          </DialogHeader>
          <FormProvider form_id="contact_footer" onSuccess={() => setIsDialogOpen(false)}>
            <form className="space-y-5 mt-4">
              <div className="space-y-2">
                <label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-default">
                  Naam
                </label>
                <input id="name" name="name" required type="text" placeholder="Uw volledige naam" className="w-full bg-muted/30 border border-border/50 rounded-md px-4 py-2.5 text-foreground font-default focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all" />
              </div>
              <div className="space-y-2">
                <label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-default">
                  E-mailadres
                </label>
                <input id="email" name="email" required type="email" placeholder="uw@email.nl" className="w-full bg-muted/30 border border-border/50 rounded-md px-4 py-2.5 text-foreground font-default focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all" />
              </div>
              <div className="space-y-2">
                <label htmlFor="message" className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-default">
                  Bericht
                </label>
                <textarea id="message" name="message" rows={4} placeholder="Hoe kunnen wij u ondersteunen?" className="w-full bg-muted/30 border border-border/50 rounded-md px-4 py-2.5 text-foreground font-default focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all resize-none" />
              </div>
              <Button type="submit" className="w-full font-default font-bold uppercase tracking-widest py-6">
                Verstuur Aanvraag
              </Button>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
    </section>;
}